import { useState, useEffect } from "react";
import Select from "react-select";
import Api from "../../Auth/Api";
import * as XLSX from 'xlsx';
import { customStyles } from '../../utils/selectStyles';

function RelatorioAluno() {
	const token = JSON.parse(localStorage.getItem("user_token"));

	const [alunos, setAlunos] = useState([]);
	const [filteredAlunos, setFilteredAlunos] = useState([]);
	const [selectedAluno, setSelectedAluno] = useState(null);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [hasFiltered, setHasFiltered] = useState(false);
	const [expandedAulas, setExpandedAulas] = useState({});
	const [expandedCertificados, setExpandedCertificados] = useState({});
	const [expandedAvaliacoesReacao, setExpandedAvaliacoesReacao] = useState({});
	const [expandedPraticas, setExpandedPraticas] = useState({});

	// Função para limitar texto com reticências
	const limitarTexto = (texto, limite) => {
		if (!texto) return "";
		return texto.length > limite ? texto.substring(0, limite) + "..." : texto;
	};

	// Carregar todos os alunos disponíveis na primeira vez
	useEffect(() => {
		async function fetchAlunos() {
			try {
				const response = await Api.get(`/api/show/usuarios`, {
					headers: {
						Authorization: `Bearer ${token}`
					}
				});
				const alunosData = response.data.map(aluno => ({
					value: aluno.codigo,
					label: `${aluno.nome_completo} (${aluno.codigo})`
				}));
				setAlunos(alunosData);
			} catch (error) {
				console.error("Erro ao carregar alunos:", error.response.data.message);
			}
		}
		fetchAlunos();
	}, [token]);

	const handleInputChange = (newValue) => {
		const value = newValue.toLowerCase();
		if (value.length >= 3) {
			setFilteredAlunos(
				alunos.filter(aluno =>
					aluno.label.toLowerCase().includes(value)
				)
			);
		} else {
			setFilteredAlunos([]);
		}
	};

	const handleAlunoChange = async (selectedOption) => {
		setSelectedAluno(selectedOption);
		setLoading(true);
		try {
			const response = await Api.get(`/api/relatorio/aluno/${selectedOption.value}`, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			});
			setData(response.data);
			setHasFiltered(true);

			const initialExpandedAulas = {};
			const initialExpandedCertificados = {};
			const initialExpandedAvaliacoesReacao = {};
			const initialExpandedPraticas = {};
			response.data.treinamentos.forEach(treinamento => {
				initialExpandedCertificados[treinamento.id_curso] = false;
				initialExpandedPraticas[treinamento.id_curso] = false;
				treinamento.modulos.forEach(modulo => {
					initialExpandedAulas[modulo.id_modulo] = false;
					initialExpandedAvaliacoesReacao[modulo.id_modulo] = false;
				});
			});
			setExpandedAulas(initialExpandedAulas);
			setExpandedCertificados(initialExpandedCertificados);
			setExpandedAvaliacoesReacao(initialExpandedAvaliacoesReacao);
			setExpandedPraticas(initialExpandedPraticas);
		} catch (error) {
			console.error("Erro ao carregar o relatório do aluno:", error.response.data.message);
		} finally {
			setLoading(false);
		}
	};

	const toggleAulasExpand = (moduloId) => {
		setExpandedAulas(prev => ({
			...prev,
			[moduloId]: !prev[moduloId]
		}));
	};

	const toggleCertificadosExpand = (cursoId) => {
		setExpandedCertificados(prev => ({
			...prev,
			[cursoId]: !prev[cursoId]
		}));
	};

	const toggleAvaliacoesReacaoExpand = (moduloId) => {
		setExpandedAvaliacoesReacao(prev => ({
			...prev,
			[moduloId]: !prev[moduloId]
		}));
	};

	const togglePraticasExpand = (cursoId) => {
		setExpandedPraticas(prev => ({
			...prev,
			[cursoId]: !prev[cursoId]
		}));
	};

	// Ativo se algum certificado ainda está na validade (na reciclagem o anterior vale até vencer)
	const verificarStatusCertificado = (certificados) => {
		if (!certificados || certificados.length === 0) return { status: "Não", texto: "Não" };

		if (certificados.some(cert => !cert.expirado)) {
			return { status: "Ativo", texto: "Sim" };
		}
		return { status: "Expirado", texto: "Expirado" };
	};

	// Data de emissão do relatório/certificado:
	// - Se o treinamento tiver prática, a emissão deve seguir a data registrada da prática
	// - Caso contrário, mantém a data de emissão do certificado
	const getDataEmissaoTreinamento = (treinamento, cert) => {
		const dataPratica = treinamento?.praticas?.[0]?.data_pratica;
		return dataPratica || cert?.data_emissao || "-";
	};

	// Formata unidades do aluno (suporta múltiplas unidades)
	const formatarUnidadesAluno = (aluno) => {
		if (aluno?.unidades?.length) {
			return aluno.unidades.join(', ');
		}
		return aluno?.unidade || '-';
	};

	// Função para exportar para Excel
	const handleExportExcel = () => {
		if (!data) return;

		// Dados do aluno para a primeira planilha
		const alunoData = [{
			"Nome do Aluno": data.aluno.nome_aluno,
			"Matrícula": data.aluno.matricula,
			"Unidade": formatarUnidadesAluno(data.aluno)
		}];

		// Preparar dados para o Excel - dados dos módulos
		const modulosData = data.treinamentos.flatMap(treinamento => {
			const statusCertificado = verificarStatusCertificado(treinamento.certificados);

			return treinamento.modulos.map(modulo => ({
				"Nome do Treinamento": treinamento.nome_treinamento,
				"Tipo de Treinamento": treinamento.tipo_treinamento.charAt(0).toUpperCase() + treinamento.tipo_treinamento.slice(1),
				"Nome do Módulo": modulo.nome_modulo,
				"Unidade": modulo.unidade,
				"Status Treinamento": modulo.status_treinamento,
				"Etapa Teórica": modulo.etapa_teorica,
				"Data Teoria": modulo.data_teoria || "-",
				"Nota Avaliação": modulo.nota_avaliacao,
				"Data Avaliação": modulo.data_avaliacao || "-",
				"Data Prática": treinamento.praticas?.[0]?.data_pratica || "-",
				"Status Certificado": statusCertificado.status
			}));
		});

		// Preparar dados para o Excel - dados das aulas
		const aulasData = data.treinamentos.flatMap(treinamento =>
			treinamento.modulos.flatMap(modulo =>
				modulo.aulas.map(aula => ({
					"Nome do Treinamento": treinamento.nome_treinamento,
					"Nome do Módulo": modulo.nome_modulo,
					"Título da Aula": aula.titulo_aula,
					"Data de Conclusão": aula.data_conclusao,
					"Status": aula.arquivado === "Sim" ? "Expirada" : "Ativa"
				}))
			)
		);

		// Preparar dados para o Excel - dados dos certificados
		const certificadosData = data.treinamentos.flatMap(treinamento =>
			treinamento.certificados.map(cert => ({
				"Nome do Treinamento": treinamento.nome_treinamento,
				"Tipo de Treinamento": treinamento.tipo_treinamento.charAt(0).toUpperCase() + treinamento.tipo_treinamento.slice(1),
				"Data de Emissão": getDataEmissaoTreinamento(treinamento, cert),
				"Status": cert.expirado ? "Expirado" : "Ativo"
			}))
		);

		// Preparar dados para o Excel - dados das práticas
		const praticasData = data.treinamentos.flatMap(treinamento =>
			treinamento.praticas.map(pratica => ({
				"Nome do Treinamento": treinamento.nome_treinamento,
				"Tipo de Treinamento": treinamento.tipo_treinamento.charAt(0).toUpperCase() + treinamento.tipo_treinamento.slice(1),
				"Data da Prática": pratica.data_pratica,
				"Local": pratica.local,
				"Gestor": pratica.gestor
			}))
		);

		// Preparar dados para o Excel - dados das avaliações de reação
		const avaliacoesReacaoData = data.treinamentos.flatMap(treinamento =>
			treinamento.modulos.flatMap(modulo =>
				(modulo.avaliacoes_reacao || []).map(avaliacao => {
					const questoes = typeof avaliacao.questoes === 'string'
						? JSON.parse(avaliacao.questoes)
						: avaliacao.questoes;
					const respostas = typeof avaliacao.respostas === 'string'
						? JSON.parse(avaliacao.respostas)
						: avaliacao.respostas;

					return {
						"Nome do Treinamento": treinamento.nome_treinamento,
						"Nome do Módulo": modulo.nome_modulo,
						"Tipo de Avaliação": avaliacao.tipo,
						"Data": avaliacao.data_formatada,
						"Questões": questoes ? questoes.join('; ') : '',
						"Respostas": respostas ? respostas.join('; ') : '',
						"Comentários": avaliacao.comentarios || "-"
					};
				})
			)
		);

		// Cria uma nova planilha
		const workbook = XLSX.utils.book_new();

		// Adiciona planilha com dados do aluno
		const worksheetAluno = XLSX.utils.json_to_sheet(alunoData);
		XLSX.utils.book_append_sheet(workbook, worksheetAluno, "Aluno");

		// Adiciona planilha de módulos
		const worksheetModulos = XLSX.utils.json_to_sheet(modulosData);
		XLSX.utils.book_append_sheet(workbook, worksheetModulos, "Módulos");

		// Adiciona planilha de aulas (todas, incluindo expiradas)
		if (aulasData.length > 0) {
			const worksheetAulas = XLSX.utils.json_to_sheet(aulasData);
			XLSX.utils.book_append_sheet(workbook, worksheetAulas, "Aulas");
		}

		// Adiciona planilha de certificados (todos, incluindo expirados)
		if (certificadosData.length > 0) {
			const worksheetCertificados = XLSX.utils.json_to_sheet(certificadosData);
			XLSX.utils.book_append_sheet(workbook, worksheetCertificados, "Certificados");
		}

		// Adiciona planilha de práticas
		if (praticasData.length > 0) {
			const worksheetPraticas = XLSX.utils.json_to_sheet(praticasData);
			XLSX.utils.book_append_sheet(workbook, worksheetPraticas, "Práticas");
		}

		// Adiciona planilha de avaliações de reação
		if (avaliacoesReacaoData.length > 0) {
			const worksheetAvaliacoesReacao = XLSX.utils.json_to_sheet(avaliacoesReacaoData);
			XLSX.utils.book_append_sheet(workbook, worksheetAvaliacoesReacao, "Avaliações de Reação");
		}

		// Gera um arquivo Excel e faz o download
        XLSX.writeFile(workbook, `relatorio_aluno_${data.aluno.matricula}.xlsx`);
    };

    return (
		<>
		<div className="fade-in w-100 mt-3" style={{ marginBottom: '100px' }}>
			<div className="rounded-3 mb-4">
				<div className="mb-3">
					<h1 className="text-primary fs-2 fw-bold mb-2">Relatório do Aluno</h1>
					<p className="text-muted mb-0">
						Selecione um aluno para visualizar e exportar os detalhes de desempenho e certificações.
					</p>
				</div>
			</div>

				{/* Filtro para selecionar o aluno */}
				<div className="filters mb-4">
					<div className="row">

						<div className="col-md-6">
							<Select
								name="aluno"
								styles={customStyles}
								options={filteredAlunos}
								classNamePrefix="select"
								placeholder="Digite pelo menos 3 dígitos para buscar..."
								value={selectedAluno}
								onChange={handleAlunoChange}
								onInputChange={handleInputChange}
								isLoading={loading}
								noOptionsMessage={() => "Digite pelo menos 3 dígitos para buscar"}
							/>
						</div>
					</div>
				</div>
				{hasFiltered && (
					<button className="btn btn-secondary mb-3" onClick={handleExportExcel}>
						<i className="bi bi-table"></i> | Baixar Excel
					</button>
				)}

				{loading ? (
					<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
						<div className="spinner-border text-primary" role="status">
							<span className="visually-hidden">Carregando...</span>
						</div>
					</div>
				) : (
					hasFiltered && data && (
					<>
						{/* Cabeçalho do Aluno */}
						<div className="mb-4">
							<h4>{data.aluno.nome_aluno}</h4>
							<p><strong>Matrícula:</strong> {data.aluno.matricula}</p>
							<p><strong>Unidade{data.aluno.unidades?.length > 1 ? 's' : ''}:</strong> {formatarUnidadesAluno(data.aluno)}</p>
							<p>
								<strong>Status:</strong>
								<span className={`badge ms-2 ${data.aluno.status === 'ativo' ? 'bg-success' : 'bg-danger'}`}>
									{data.aluno.status === 'ativo' ? 'Ativo' : 'Inativo'}
								</span>
							</p>
						</div>

						{/* Tabelas de Treinamentos */}
						{data.treinamentos.map((treinamento, idx) => (
							<div className="table-full-width mt-3 mb-5" key={idx}>
								<div className="table-responsive">
									<div className="mb-3">
										{/* Cabeçalho do Treinamento */}
										<div className="bg-primary text-white p-3 rounded-top d-flex justify-content-between align-items-center">
											<h5 className="m-0">{treinamento.nome_treinamento} ({treinamento.tipo_treinamento === 'misto' ? 'Híbrido' : 'Online'})</h5>
											<div>
												{/* Botão para exibir práticas */}
												{treinamento.praticas && treinamento.praticas.length > 0 && (
													<button
														className="btn btn-sm btn-light ms-2"
														onClick={() => togglePraticasExpand(treinamento.id_curso)}
													>
														{expandedPraticas[treinamento.id_curso] ? 'Ocultar Práticas' : 'Ver Práticas'}
														<i className={`bi bi-chevron-${expandedPraticas[treinamento.id_curso] ? 'up' : 'down'} ms-1`}></i>
													</button>
												)}
												{/* Botão para exibir certificados */}
												{treinamento.certificados.length > 0 && (
													<button
														className="btn btn-sm btn-light ms-2"
														onClick={() => toggleCertificadosExpand(treinamento.id_curso)}
													>
														{expandedCertificados[treinamento.id_curso] ? 'Ocultar Certificados' : 'Ver Certificados'}
														<i className={`bi bi-chevron-${expandedCertificados[treinamento.id_curso] ? 'up' : 'down'} ms-1`}></i>
													</button>
												)}
											</div>
										</div>

										{/* Lista de práticas */}
										{expandedPraticas[treinamento.id_curso] && treinamento.praticas && treinamento.praticas.length > 0 && (
											<div className="border border-top-0 p-3 bg-light">
												<h6 className="mb-3">Práticas Registradas:</h6>
												<table className="table table-sm table-striped">
													<thead>
														<tr>
															<th>Data da Prática</th>
															<th>Local</th>
															<th>Gestor</th>
															<th>Status</th>
														</tr>
													</thead>
													<tbody>
														{treinamento.praticas.map((pratica, praticaIdx) => (
															<tr key={praticaIdx}>
																<td>{pratica.data_pratica}</td>
																<td>{pratica.local || "-"}</td>
																<td>{pratica.gestor || "-"}</td>
																<td>{pratica.arquivado === 1 || pratica.arquivado === "Sim" ?
																	<span className="badge bg-danger">Arquivada</span> :
																	<span className="badge bg-success">Ativa</span>}
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}

										{/* Lista de certificados */}
										{expandedCertificados[treinamento.id_curso] && treinamento.certificados.length > 0 && (
											<div className="border border-top-0 p-3 bg-light">
												<h6 className="mb-3">Certificados Emitidos:</h6>
												<table className="table table-sm table-striped">
													<thead>
														<tr>
															<th>Data de Emissão</th>
															<th>Status</th>
														</tr>
													</thead>
													<tbody>
														{treinamento.certificados.map((cert, certIdx) => (
															<tr key={certIdx}>
																<td>{getDataEmissaoTreinamento(treinamento, cert)}</td>
																<td>{cert.expirado ?
																	<span className="badge bg-danger">Expirado</span> :
																	<span className="badge bg-success">Ativo</span>}
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}

										{/* Tabela de Módulos */}
										<table className="table align-middle table-striped table-hover">
											<thead>
												<tr className='border border-white text-nowrap'>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Nome do Módulo</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Unidade</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Status Treinamento</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Etapa Teórica</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Data Teoria</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted text-center'>Nota Avaliação</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Data Avaliação</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted text-center'>Aulas</th>
													<th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted text-center'>Avaliação de Reação</th>
												</tr>
											</thead>
											<tbody>
												{treinamento.modulos.map((modulo, modIdx) => (
													<>
														<tr key={modIdx}>
															<td title={modulo.nome_modulo} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																{limitarTexto(modulo.nome_modulo, 30)}
															</td>
															<td>{modulo.unidade}</td>
															<td>{modulo.status_treinamento}</td>
															<td>{modulo.etapa_teorica}</td>
															<td>{modulo.data_teoria || "-"}</td>
															<td className="text-center">{modulo.nota_avaliacao}</td>
															<td>{modulo.data_avaliacao || "-"}</td>
															<td className="text-center">
																{modulo.aulas.length > 0 && (
																	<button
																		className="btn btn-sm btn-outline-primary"
																		onClick={() => toggleAulasExpand(modulo.id_modulo)}
																	>
																		{expandedAulas[modulo.id_modulo] ? 'Ocultar' : 'Ver'}
																		<i className={`bi bi-chevron-${expandedAulas[modulo.id_modulo] ? 'up' : 'down'} ms-1`}></i>
																	</button>
																)}
															</td>
															<td className="text-center">
																{modulo.avaliacoes_reacao && modulo.avaliacoes_reacao.length > 0 && (
																	<button
																		className="btn btn-sm btn-outline-primary"
																		onClick={() => toggleAvaliacoesReacaoExpand(modulo.id_modulo)}
																	>
																		{expandedAvaliacoesReacao[modulo.id_modulo] ? 'Ocultar' : 'Ver'}
																		<i className={`bi bi-chevron-${expandedAvaliacoesReacao[modulo.id_modulo] ? 'up' : 'down'} ms-1`}></i>
																	</button>
																)}
															</td>
														</tr>
														{/* Linha de detalhes das aulas */}
														{expandedAulas[modulo.id_modulo] && (
															<tr>
																<td colSpan="9" className="p-0">
																	<div className="p-3 bg-light">
																		<h6 className="mb-3">Aulas Concluídas:</h6>
																		<table className="table table-sm table-striped">
																			<thead>
																				<tr>
																					<th>Título da Aula</th>
																					<th>Data de Conclusão</th>
																					<th>Status</th>
																				</tr>
																			</thead>
																			<tbody>
																				{modulo.aulas.map((aula, aulaIdx) => (
																					<tr key={aulaIdx}>
																						<td title={aula.titulo_aula} style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																							{limitarTexto(aula.titulo_aula, 40)}
																						</td>
																						<td>{aula.data_conclusao}</td>
																						<td>{aula.arquivado === "Sim" ?
																							<span className="badge bg-danger">Expirada</span> :
																							<span className="badge bg-success">Ativa</span>}
																						</td>
																					</tr>
																				))}
																			</tbody>
																		</table>
																	</div>
																</td>
															</tr>
														)}
														{/* Linha de detalhes das avaliações de reação */}
														{expandedAvaliacoesReacao[modulo.id_modulo] && modulo.avaliacoes_reacao && modulo.avaliacoes_reacao.length > 0 && (
															<tr>
																<td colSpan="9" className="p-0">
																	<div className="p-3 bg-light">
																		<h6 className="mb-3">Avaliações de Reação:</h6>
																		{modulo.avaliacoes_reacao.map((avaliacao, avIdx) => {
																			const questoes = typeof avaliacao.questoes === 'string'
																				? JSON.parse(avaliacao.questoes)
																				: avaliacao.questoes;
																			const respostas = typeof avaliacao.respostas === 'string'
																				? JSON.parse(avaliacao.respostas)
																				: avaliacao.respostas;

																			return (
																				<div key={avIdx} className="mb-4">
																					<div className="d-flex justify-content-between align-items-center mb-2">
																						<strong>Tipo: {avaliacao.tipo}</strong>
																						<span className="text-muted">Data: {avaliacao.data_formatada}</span>
																					</div>
																					<table className="table table-sm table-striped">
																						<thead>
																							<tr>
																								<th>Pergunta</th>
																								<th>Resposta</th>
																							</tr>
																						</thead>
																						<tbody>
																							{questoes && respostas && questoes.map((questao, qIdx) => (
																								<tr key={qIdx}>
																									<td>{questao}</td>
																									<td>{respostas[qIdx] || '-'}</td>
																								</tr>
																							))}
																						</tbody>
																					</table>
																					{avaliacao.comentarios && (
																						<div className="mt-2">
																							<strong>Comentários:</strong>
																							<p className="mb-0">{avaliacao.comentarios}</p>
																						</div>
																					)}
																				</div>
																			);
																		})}
																	</div>
																</td>
															</tr>
														)}
													</>
												))}
											</tbody>
											<tfoot>
												<tr>
													<th colSpan="9" className='bg-primary text-white fw-normal rounded-bottom'>
														<div className="table-footer d-flex justify-content-between align-items-center">
															<div>
																{treinamento.praticas && treinamento.praticas.length > 0 ? (
																	<span>Práticas: {treinamento.praticas.length} {treinamento.praticas.length === 1 ? 'registrada' : 'registradas'}</span>
																) : (
																	<span>Práticas: Nenhuma</span>
																)}
															</div>
															<div className="d-flex align-items-center">
																{treinamento.certificados.length > 0 ? (
																	<span>Certificado: {verificarStatusCertificado(treinamento.certificados).texto}</span>
																) : (
																	<span>Certificado: Não</span>
																)}
															</div>
														</div>
													</th>
												</tr>
											</tfoot>
										</table>

									</div>
								</div>
							</div>
						))}
					</>
					)
				)}
			</div>
		</>
	);
}

export default RelatorioAluno;